import { Badge, Group, Paper, Skeleton, Text, Tooltip } from "@mantine/core";
import { IconFileText, IconStack2 } from "@tabler/icons-react";
import { Handle, type Node, type NodeProps, Position } from "@xyflow/react";
import { type ExplodeNodeData, NodeRole } from "./types";

const ROLE_STYLE = {
    [NodeRole.ROOT]: { bg: "purple.7", fg: "white", border: "#5f3dc4" },
    [NodeRole.GROUP]: { bg: "white", fg: undefined, border: "#5f3dc4" },
    [NodeRole.TITLE]: { bg: "white", fg: undefined, border: "#5f3dc4" },
};

const hiddenHandle = { opacity: 0 };

const ghostKeyframes =
    "@keyframes documentGraphGhostIn { to { opacity: 1; transform: translateY(0) scale(1); } }";

export const BucketNode = ({ data }: NodeProps<Node<ExplodeNodeData>>) => {
    const style = ROLE_STYLE[data.role];
    const isRoot = data.role === NodeRole.ROOT;
    const isTitle = data.title === true;

    if (isTitle) {
        const filled = data.focused === true;
        const valuesLabel =
            data.count !== undefined
                ? `${data.label}, ${data.count.toLocaleString()} ${data.count === 1 ? "value" : "values"}`
                : data.label;
        const titleTooltip = data.overlay ? `${valuesLabel} — click to browse` : valuesLabel;
        return (
            <Paper
                withBorder
                p="sm"
                radius="md"
                bg={filled ? "violet.6" : "violet.0"}
                style={{
                    width: 200,
                    borderColor: "#7048e8",
                    borderWidth: 2,
                    cursor: "pointer",
                    opacity: data.dimmed ? 0.1 : 1,
                    pointerEvents: data.dimmed ? "none" : "auto",
                    transition: "opacity 350ms ease, background-color 200ms ease",
                }}
            >
                <Handle type="target" position={Position.Top} style={hiddenHandle} />
                <Group justify="space-between" gap="xs" wrap="nowrap" align="center">
                    <Tooltip label={titleTooltip} withArrow multiline maw={260} openDelay={250}>
                        <Text
                            size="sm"
                            fw={700}
                            tt="uppercase"
                            lineClamp={2}
                            c={filled ? "white" : "violet.9"}
                            style={{ flex: 1, minWidth: 0, letterSpacing: "0.3px" }}
                        >
                            {data.label}
                        </Text>
                    </Tooltip>
                    {data.count !== undefined && (
                        <Badge
                            size="md"
                            variant={filled ? "white" : "filled"}
                            color="violet"
                            leftSection={<IconStack2 size={12} />}
                            style={{ flexShrink: 0 }}
                            styles={{ label: { overflow: "visible" } }}
                        >
                            {data.count.toLocaleString()}
                        </Badge>
                    )}
                </Group>
                <Handle type="source" position={Position.Bottom} style={hiddenHandle} />
            </Paper>
        );
    }

    if (data.skeleton) {
        return (
            <Paper
                withBorder
                p="md"
                radius="md"
                bg="white"
                style={{
                    width: 200,
                    borderColor: "#9775fa",
                    borderWidth: 2,
                    borderStyle: "dashed",
                    cursor: "default",
                    pointerEvents: "none",
                    opacity: 0,
                    transform: "translateY(6px) scale(0.98)",
                    animation: "documentGraphGhostIn 320ms ease forwards",
                    transition: "opacity 320ms ease, transform 320ms ease",
                }}
            >
                <style>{ghostKeyframes}</style>
                <Handle type="target" position={Position.Top} style={hiddenHandle} />
                <Skeleton height={24} radius="sm" animate />
                <Handle type="source" position={Position.Bottom} style={hiddenHandle} />
            </Paper>
        );
    }

    if (data.muted) {
        return (
            <Paper
                withBorder
                p="md"
                radius="md"
                bg="gray.0"
                style={{
                    width: 200,
                    borderColor: "#ced4da",
                    borderWidth: 2,
                    borderStyle: "dashed",
                    cursor: "pointer",
                    opacity: data.dimmed ? 0.1 : 1,
                    pointerEvents: data.dimmed ? "none" : "auto",
                    transition: "opacity 350ms ease",
                }}
            >
                <Handle type="target" position={Position.Top} style={hiddenHandle} />
                <Tooltip label={data.label} withArrow openDelay={250}>
                    <Text size="sm" fw={600} c="dimmed" ta="center" style={{ minWidth: 0 }}>
                        {data.label}
                    </Text>
                </Tooltip>
                <Handle type="source" position={Position.Bottom} style={hiddenHandle} />
            </Paper>
        );
    }

    const highlighted = data.focused === true;

    return (
        <Paper
            withBorder
            p="md"
            radius="md"
            bg={highlighted ? "violet.0" : style.bg}
            style={{
                width: 200,
                borderColor: highlighted ? "#5f3dc4" : style.border,
                borderWidth: highlighted ? 3 : 2,
                cursor: isRoot ? "default" : "pointer",
                opacity: data.dimmed ? 0.1 : 1,
                pointerEvents: data.dimmed ? "none" : "auto",
                transition: "opacity 350ms ease, border-color 250ms ease",
            }}
        >
            {data.caption && (
                <Text
                    size="xs"
                    fw={600}
                    c="dimmed"
                    tt="uppercase"
                    style={{
                        position: "absolute",
                        top: -40,
                        left: 0,
                        width: "100%",
                        textAlign: "center",
                        letterSpacing: 0.5,
                        opacity: 0.75,
                    }}
                >
                    {data.caption}
                </Text>
            )}
            <Handle type="target" position={Position.Top} style={hiddenHandle} />
            <Group justify="space-between" gap="sm" wrap="nowrap" align="center">
                <Tooltip
                    label={
                        data.count !== undefined
                            ? `${data.label}, ${data.count.toLocaleString()} ${data.count === 1 ? "document" : "documents"}`
                            : data.label
                    }
                    withArrow
                    multiline
                    maw={260}
                    openDelay={250}
                >
                    <Text
                        size="md"
                        fw={700}
                        lineClamp={2}
                        c={style.fg}
                        style={{ flex: 1, minWidth: 0 }}
                    >
                        {data.label}
                    </Text>
                </Tooltip>
                {data.count !== undefined && (
                    <Badge
                        size="lg"
                        variant={isRoot ? "white" : "filled"}
                        color="purple"
                        leftSection={<IconFileText size={13} />}
                        style={{ flexShrink: 0 }}
                        styles={{ label: { overflow: "visible" } }}
                    >
                        {data.count.toLocaleString()}
                    </Badge>
                )}
            </Group>
            <Handle type="source" position={Position.Bottom} style={hiddenHandle} />
        </Paper>
    );
};
